<template lang="pug">
  .layout
    y-dialog(fill-height v-model="showModal" transition="fade-transition" persistent max-width="600px" :fullscreen="$vuetify.breakpoint.xsOnly")
      v-card(tile)
        v-card-title.px-5(:class="editedBundles.length > 0 ? 'background-color: success500' : ''")
          v-row(no-gutters align="center")
            v-col.shrink
              v-btn.ml-n3(text icon @click="back" height)
                v-icon(:color="editedBundles.length > 0 ? 'white' : 'basic800'" :size="24") mdi-arrow-left
            v-col.ml-3(v-if="editedBundles.length > 0")
              v-row(no-gutters)
                v-col
                  p.mb-0.h6.white--text {{ $t('save_changes') }} ({{ editedBundles.length }})
                v-col.shrink.d-flex(align='right')
                  v-btn.button-AA-tiny.mr-1(outlined small height="24" color='basic100T08' :disabled="isLoading" @click='clearTask') {{ $t('cancel') }}
                  v-btn.button-AA-tiny.basic800--text(depressed small color="basic100" height="24" :loading="isLoading" @click="saveTask") {{ $t('save') }}
            v-col.ml-3(v-else)
              p.mb-0.h6.basic800--text Set {{ title }}
        v-container.pa-0(style="max-width:600px")
          div.px-4.py-3(v-if="selectedSetting === 'weight'" style="background: var(--v-infoT08)")
            div.p2.info500--text {{ $t('alert_additional_weight_text') }}
          div.px-4.pt-2
            v-text-field.my-2(v-model="searchText" hide-details dense outlined background-color="basic200" height="40" :placeholder="$t('search_bundle')")
              template(v-slot:append)
                  eva-icon(name="search-outline" :fill="$vuetify.theme.themes[$theme].basic600")
          v-divider.my-4
          //- Apply all
          bundle-bulk-apply-all(ref="multiple" :selectedSetting="selectedSetting" :isFetching="isFetching" @updateData="applyAllTask")
          //- Or
          div.d-flex.align-center.pa-4
            v-divider
            div.basic600--text.px-2.c2.text-uppercase {{ $t('or') }}
            v-divider
          //- Loading state
          v-layout.py-6(v-if="isFetching")
            v-row.fill-height.ma-0(align='center' justify='center')
              v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
          //- Individual
          div.pa-4(v-else)
            bundle-bulk-individual-header(:selectedSetting="selectedSetting")
            //- Empty list or search not found
            template(v-if="bundles.length === 0")
              div.py-6.d-flex.flex-column.align-center
                v-row.fill-height.ma-0(align='center' justify='center')
                  div.mx-auto
                    v-img.mx-auto(:src="require('@/assets/images/icon_empty.png')" style="width:80px; height:80px" contain)
                    p.caption {{ $t('bundle_not_found') }}
            template(v-else v-for="bundle in bundles")
              bundle-bulk-individual-item(ref="individual" :parentBundle="bundle" :selectedSetting="selectedSetting" :edited="editedBundles[editedBundles.findIndex(edited => edited.id===bundle.id)]" :focused="editedBundles.some(edited => edited.id===bundle.id)" @editValue="updateTask" @removeValue="removeTask")
        y-pagination-custom.pa-4(v-if="count > 0 && !isFetching" v-model="page" :parentPageSize.sync="pageSize" :count="count" :text="'Show items'")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import BundleBulkApplyAll from '@/components/offers/bundles/BundleBulkApplyAll.vue'
import BundleBulkIndividualHeader from '@/components/offers/bundles/BundleBulkIndividualHeader.vue'
import BundleBulkIndividualItem from '@/components/offers/bundles/BundleBulkIndividualItem.vue'

export default {
  name: 'BundleBulkSetting',
  components: {
    BundleBulkApplyAll,
    BundleBulkIndividualHeader,
    BundleBulkIndividualItem
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    selectedSetting: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isFetching: false,
      fetchUrl: '/products/',
      bundles: [],
      editedBundles: [],
      page: 1,
      pageSize: 20,
      count: 0,
      searchText: '',
      searchCountDown: null,
      isLoading: false
    }
  },
  mounted() {
    this.page = this.$route.query.page ? parseInt(this.$route.query.page) : 1
    this.fetchBundles()
  },
  watch: {
    page(val) {
      history.pushState({}, null, location.pathname + '?page=' + val)
      if (!this.isFetching) {
        this.fetchBundles()
      }
    },
    pageSize() {
      this.page = 1
      this.fetchBundles()
    },
    searchText: {
      handler() {
        if (this.searchCountDown) {
          clearTimeout(this.searchCountDown)
        }
        this.searchCountDown = setTimeout(() => {
          this.page = 1
          this.fetchBundles()
        }, 200)
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    filterParams() {
      const params = {
        company: this.company.id,
        bundle: true,
        ordering: '-id',
        page: this.page,
        page_size: this.pageSize,
        search: this.searchText
      }
      if (this.selectedSetting === 'inventory') {
        params.inventory_enabled = true
      }
      return params
    },
    showModal: {
      get() {
        return this.open
      },
      set(val) {
        return this.$emit('update:open', val)
      }
    },
    title() {
      switch (this.selectedSetting) {
        case 'price':
          return this.$t('bundle_price')

        case 'weight':
          return this.$t('additional_weight')

        case 'inventory':
          return this.$t('available_bundles')

        default:
          return false
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    back() {
      this.$router.replace({ query: null }).catch(() => {})
      this.showModal = false
    },
    filterProperties(obj) {
      const allowedProperties = ['id', 'name', 'image', 'bundle', 'price', 'weight', 'inventory_qty']
      const filteredObj = this.$options.filters.filterAllowedObjectProperties(obj, allowedProperties)
      // Custom properties
      switch (this.selectedSetting) {
        case 'price':
          filteredObj.value = filteredObj.price / this.$currency.multiplier
          break

        case 'weight':
          filteredObj.value = filteredObj.weight
          break

        case 'inventory':
          filteredObj.value = filteredObj.inventory_qty
          break

        default:
          break
      }
      return filteredObj
    },
    fetchBundles() {
      this.isFetching = true
      this.axios
        .get(this.fetchUrl, { params: this.filterParams })
        .then(resp => {
          this.bundles = resp.data.results.map(bundle => {
            return this.filterProperties(bundle)
          })
          this.count = resp.data.count
          this.isFetching = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    removeTask(bundle) {
      const index = this.editedBundles.findIndex(edited => edited.id === bundle.id)
      if (index !== -1) {
        this.editedBundles.splice(index, 1)
      }
    },
    updateTask(bundle) {
      const index = this.editedBundles.findIndex(edited => edited.id === bundle.id)
      if (index === -1) {
        this.editedBundles.push(bundle)
      } else {
        this.editedBundles[index].value = bundle.value
      }
    },
    applyAllTask(value) {
      this.bundles.forEach((bundle, bundleIndex) => {
        let isChanged = false
        switch (this.selectedSetting) {
          case 'price':
            if (value !== bundle.price / this.$currency.multiplier) {
              isChanged = true
            }
            break

          case 'weight':
            if (value !== bundle.weight) {
              isChanged = true
            }
            break

          case 'inventory':
            if (value !== bundle.inventory_qty) {
              isChanged = true
            }
            break

          default:
            break
        }
        if (isChanged) {
          const index = this.editedBundles.findIndex(edited => edited.id === bundle.id)
          if (index === -1) {
            this.editedBundles.push({ id: bundle.id, value })
          } else {
            this.editedBundles[index].value = value
          }
          this.$refs.individual[bundleIndex].value = value
        }
      })
    },
    clearTask() {
      this.editedBundles = []
      this.bundles = []
      this.fetchBundles()
    },
    async saveTask() {
      this.isLoading = true
      let param
      switch (this.selectedSetting) {
        case 'price':
          param = 'price'
          break

        case 'weight':
          param = 'weight'
          break

        case 'inventory':
          param = 'inventory_qty'
          break

        default:
          break
      }
      try {
        for (const bundle of this.editedBundles) {
          let value = bundle.value
          if (this.selectedSetting === 'price') {
            value = bundle.value.toFixed(this.$currency.exponent).replace('.', '')
          }
          await this.axios.patch(`/products/${bundle.id}/`, { [param]: value })
        }
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
      this.$emit('refetch')
      this.isLoading = false
      this.$refs.multiple.$refs.form.reset()
      this.clearTask()
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>

<i18n>
{
  "en": {
    "alert_additional_weight_text": "Enter any additional weight that might come with a bundle such as special packaging or free gifts. This is important to calculate shipping rates.",
    "bundle_price": "bundle price",
    "additional_weight": "additional weight",
    "available_bundles": "available bundles"
  },
  "id": {
    "alert_additional_weight_text": "Masukkan berat tambahan apa pun yang mungkin disertakan dengan paket seperti kemasan khusus atau hadiah gratis. Ini penting untuk menghitung tarif pengiriman.",
    "bundle_price": "harga Bundle",
    "additional_weight": "berat tambahan",
    "available_bundles": "Bundle yang tersedia"
  }
}
</i18n>